import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface VirtualListOptions {
  count: number;
  rowHeight: number;
  /** Rows rendered beyond each edge, so a fast flick does not show gaps. */
  overscan?: number;
}

export interface VirtualList {
  /** Attach to the scroll container. */
  ref: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  start: number;
  end: number;
  /** Height of the full list, so the scrollbar reflects the real row count. */
  totalHeight: number;
  /** Translate applied to the rendered window. */
  offsetY: number;
  scrollToTop: () => void;
}

/**
 * Fixed-height windowing.
 *
 * A live catalog is routinely 20,000 channels; rendering that many rows costs
 * seconds of layout and hundreds of megabytes. Only the visible slice is
 * mounted, and the scrollbar is kept honest with a spacer of the full height.
 *
 * Scroll handling is rAF-coalesced rather than run per event: a trackpad emits
 * scroll events far faster than frames, and doing React work on each one is
 * exactly what turns a 60fps list into a janky one.
 */
export function useVirtualList({ count, rowHeight, overscan = 8 }: VirtualListOptions): VirtualList {
  const ref = useRef<HTMLDivElement | null>(null);
  const frame = useRef(0);
  const [range, setRange] = useState({ start: 0, end: 0 });

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const first = Math.max(0, Math.floor(element.scrollTop / rowHeight) - overscan);
    const visible = Math.ceil(element.clientHeight / rowHeight) + overscan * 2;
    const last = Math.min(count, first + visible);
    setRange((previous) =>
      previous.start === first && previous.end === last ? previous : { start: first, end: last },
    );
  }, [count, rowHeight, overscan]);

  const onScroll = useCallback(() => {
    if (frame.current) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = 0;
      measure();
    });
  }, [measure]);

  // Layout effect: measuring after paint would render an empty window for one
  // frame every time the list is swapped out.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(
    () => () => {
      if (frame.current) window.cancelAnimationFrame(frame.current);
    },
    [],
  );

  const scrollToTop = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    element.scrollTop = 0;
    measure();
  }, [measure]);

  return {
    ref,
    onScroll,
    start: range.start,
    end: range.end,
    totalHeight: count * rowHeight,
    offsetY: range.start * rowHeight,
    scrollToTop,
  };
}
